// iOS Safari viewport height fix
// This handles the dynamic viewport changes when Safari UI shows/hides

export const setupIOSViewport = () => {
  // Only run on iOS devices
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  
  if (!isIOS) return;

  // Check if running as PWA
  const isPWA = window.matchMedia('(display-mode: standalone)').matches;

  const setViewportHeight = () => {
    // Get the actual viewport height
    let vh = window.innerHeight * 0.01;
    
    // In PWA mode, use the full screen height
    if (isPWA) {
      vh = window.screen.height * 0.01;
    }
    
    // Set the CSS custom property
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  };

  // Set initial viewport height
  setViewportHeight();

  // In PWA mode, force full screen
  if (isPWA) {
    // Update viewport meta tag for PWA
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      viewport.setAttribute('content', 'width=device-width, initial-scale=1, user-scalable=no, viewport-fit=cover, minimal-ui');
    }
    
    // Force body to use full screen
    document.body.style.height = '100vh';
    document.body.style.height = '100dvh';
    document.documentElement.style.height = '100vh';
    document.documentElement.style.height = '100dvh';
  }

  // Update CSS var with actual nav height so CSS can reserve exact scroll padding
  const updateNavHeightVar = (bottomNav) => {
    try {
      const rect = bottomNav?.getBoundingClientRect?.();
      if (rect && Number.isFinite(rect.height) && rect.height > 0) {
        document.documentElement.style.setProperty('--nav-height', `${rect.height}px`);
      }
    } catch (_) {
      // no-op
    }
  };

  // Ensure header stays at top (fixes keyboard viewport shift issue)
  const ensureHeaderAtTop = () => {
    const header = document.querySelector('.unified-header');
    if (header) {
      // Remove any inline styles that might have been set by other code
      header.style.removeProperty('transform');
      // Ensure header stays at top with proper positioning
      header.style.setProperty('position', 'fixed', 'important');
      header.style.setProperty('top', '0', 'important');
      header.style.setProperty('left', '0', 'important');
      header.style.setProperty('right', '0', 'important');
    }
  };

  // Ensure bottom navigation is properly positioned and report its height
  const ensureBottomNavVisible = () => {
    const bottomNav = document.querySelector('.bottom-navigation');
    if (bottomNav) {
      // Fix iOS safe area positioning issue
      bottomNav.style.setProperty('position', 'fixed', 'important');
      bottomNav.style.setProperty('bottom', '0px', 'important');
      // Update CSS variable with current height
      updateNavHeightVar(bottomNav);
    }
  };

  // Run immediately and on DOM changes
  ensureHeaderAtTop();
  ensureBottomNavVisible();
  
  // Use MutationObserver to ensure header and navigation stay properly positioned
  const observer = new MutationObserver(() => {
    ensureHeaderAtTop();
    ensureBottomNavVisible();
  });
  observer.observe(document.body, { 
    childList: true, 
    subtree: true, 
    attributes: true, 
    attributeFilter: ['style', 'class'] 
  });

  // Keep CSS var in sync on viewport changes
  window.addEventListener('resize', () => {
    ensureHeaderAtTop();
    ensureBottomNavVisible();
  });
  window.addEventListener('orientationchange', () => {
    ensureHeaderAtTop();
    ensureBottomNavVisible();
  });

  // Update on resize (when Safari UI shows/hides)
  window.addEventListener('resize', setViewportHeight);
  window.addEventListener('orientationchange', () => {
    // Delay to ensure orientation change is complete
    setTimeout(setViewportHeight, 100);
  });

  // Handle focus events that might trigger Safari UI changes
  window.addEventListener('focusin', () => {
    setTimeout(() => {
      setViewportHeight();
      ensureHeaderAtTop();
    }, 300);
  });
  
  window.addEventListener('focusout', () => {
    setTimeout(() => {
      setViewportHeight();
      ensureHeaderAtTop();
    }, 300);
  });

  // Prevent zoom on input focus
  const preventZoom = () => {
    const inputs = document.querySelectorAll('input, textarea, select');
    inputs.forEach(input => {
      input.addEventListener('focus', () => {
        input.style.fontSize = '16px';
      });
      input.addEventListener('blur', () => {
        input.style.fontSize = '';
      });
    });
  };

  // Run after DOM is loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', preventZoom);
  } else {
    preventZoom();
  }
};

// PWA prompt functionality moved to PWAInstallPrompt.tsx component
